import { Injectable, NgZone } from "@angular/core";
import { needsPhoneCompletion } from "../helpers/phone-helper";
import { needsProfileCompletion } from "../helpers/profile-helper";
import { LocalStorageService } from "./localStorage.service";
import { PhoneCompletionGateService } from "./phone-completion-gate.service";
import { ProfileCompletionGateService } from "./profile-completion-gate.service";

/**
 * Runs forced profile/phone modals after auth, on the destination shell (not LoginPage).
 * LoginPage is destroyed by navigateRoot before a short setTimeout fires on Android.
 */
@Injectable({
  providedIn: "root",
})
export class AccountCompletionOrchestratorService {
  private scheduled = false;

  constructor(
    private ngZone: NgZone,
    private localStorageService: LocalStorageService,
    private profileCompletionGateService: ProfileCompletionGateService,
    private phoneCompletionGateService: PhoneCompletionGateService
  ) {}

  needsCompletionForCurrentUser(): boolean {
    const userData = this.localStorageService.getUserData();
    if (!userData?.client || !userData?.token) {
      return false;
    }
    return (
      needsProfileCompletion(userData.client) ||
      needsPhoneCompletion(userData.client)
    );
  }

  /** Debounced; safe to call from ionViewDidEnter on home shells. */
  scheduleCompletionGates(delayMs = 450): void {
    if (!this.needsCompletionForCurrentUser()) {
      return;
    }
    if (this.scheduled) {
      return;
    }
    this.scheduled = true;
    setTimeout(() => {
      this.scheduled = false;
      this.ngZone.run(() => {
        void this.runCompletionGates();
      });
    }, delayMs);
  }

  async runCompletionGates(): Promise<void> {
    if (!this.needsCompletionForCurrentUser()) {
      return;
    }
    const profileCompleted =
      await this.profileCompletionGateService.ensureProfileCompletionForCurrentUser();
    if (!profileCompleted) {
      return;
    }
    await this.phoneCompletionGateService.ensurePhoneCompletionForCurrentUser();
  }
}
